import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, Trash2, Users, MapPin, MessageSquare, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Admin → Peer Circles Groups moderation (iter 76 Phase 3).
 *
 * Read /api/admin/peer-circles/groups, delete via DELETE /api/peer-circles/groups/{id}.
 * Deleting cascades all messages on the backend.
 */
const AdminPeerCirclesGroups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      const url = `${API}/api/admin/peer-circles/groups${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, { credentials: 'include', headers: authHeaders() });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not load groups');
      }
      const d = await res.json();
      setGroups(d.groups || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const onDelete = async (g) => {
    const phrase = `Delete "${g.name}"? This permanently removes the group and all its messages.`;
    if (!window.confirm(phrase)) return;
    setBusyId(g.group_id);
    try {
      const res = await fetch(`${API}/api/peer-circles/groups/${g.group_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not delete');
      }
      setGroups((cur) => cur.filter((x) => x.group_id !== g.group_id));
      toast.success('Group deleted.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14" data-testid="admin-peer-circles-groups">
        <header className="mb-8">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            Admin · Peer Circles
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
            Groups moderation
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
            Member-created interest groups. Delete clears the group and every message attached to it.
          </p>
        </header>

        <div className="mb-6 max-w-md relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1F2E]/40" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchGroups(); }}
            placeholder="Search name / city / description…"
            className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-full pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/20"
            data-testid="admin-groups-search"
          />
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
          </div>
        )}
        {!loading && error && (
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 mb-6 rounded-xl text-sm" data-testid="admin-groups-error">
            {error}
          </div>
        )}
        {!loading && !error && groups.length === 0 && (
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-12 text-center" data-testid="admin-groups-empty">
            <Users className="w-10 h-10 text-[#1A5C6E]/40 mx-auto mb-3" />
            <p className="text-[#0A1F2E]/65">No groups yet.</p>
          </div>
        )}
        {!loading && !error && groups.length > 0 && (
          <ul className="space-y-3" data-testid="admin-groups-list">
            {groups.map((g) => (
              <li
                key={g.group_id}
                className="bg-white border border-[#EFE6CF] rounded-2xl p-4 sm:p-5 flex items-start gap-4"
                data-testid={`admin-group-row-${g.group_id}`}
              >
                {g.photo_url ? (
                  <img src={g.photo_url} alt="" className="w-16 h-16 rounded-lg object-cover ring-2 ring-[#EFE6CF] shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-[#F4ECD8] flex items-center justify-center shrink-0">
                    <Users className="w-6 h-6 text-[#1A5C6E]/45" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display italic text-xl text-[#0A1F2E] leading-tight">{g.name}</h3>
                    <a
                      href={`/portal/peer-circles/groups/${g.group_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#1A5C6E] hover:text-[#0A1F2E] inline-flex items-center"
                      title="Open in portal"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-[#0A1F2E]/60 mt-1">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {g.city}</span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {g.member_count}</span>
                    <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {g.message_count}</span>
                    {g.creator && (
                      <span>· by {g.creator.first_name} {g.creator.last_name_initial}.</span>
                    )}
                    {g.created_at && (
                      <span>· {new Date(g.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago'})}</span>
                    )}
                  </div>
                  <p className="text-sm text-[#0A1F2E]/70 mt-2 line-clamp-2">{g.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onDelete(g)}
                  disabled={busyId === g.group_id}
                  className="shrink-0 inline-flex items-center gap-1 bg-white border border-[#B91C1C]/35 hover:bg-[#B91C1C]/10 disabled:opacity-50 text-[#B91C1C] font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-2 rounded-full transition"
                  data-testid={`admin-group-delete-${g.group_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminPeerCirclesGroups;
